"use client";
import { useState } from "react";
import { PrismicRichText } from "@prismicio/react";
import BillingToggle from "@/slices/Pricing/_components/BillingToggle";
import TextComponent from "./TextComponent";
import cn from "@/utils/cn";
import { HiOutlineChevronRight } from "react-icons/hi2";
import Button from "@/components/buttons";
import FeatureMatrixComponent from "@/slices/FeatureMatrix/feature-matrix";
import { trackGAEvent } from "@/utils/ga";

type Step = "selection" | "payment" | "confirmation";

export interface PricingPlanItem {
  title: any;
  monthly_price: { text: string }[];
  annual_price: { text: string }[];
  monthly_text: any;
  annually_text: any;
  subtitle: any;
  description: any;
}

interface PricingPlansProps {
  items: PricingPlanItem[];
  onSelectPlan: (planName: string, billingCycle: "MONTHLY" | "ANNUAL") => void;
  showFeatureMatrix?: boolean;
  setShowFeatureMatrix?: any;
  subscriptionPlan?: string | null;
}

export function PricingPlans({
  items,
  onSelectPlan,
  showFeatureMatrix,
  setShowFeatureMatrix,
  subscriptionPlan,
}: PricingPlansProps) {
  const [billing, setBilling] = useState<"monthly" | "annually">("annually");
  const [step, setStep] = useState<Step>("selection");

  const [selectedCardIndex, setSelectedCardIndex] = useState(1);
  return (
    <div className={cn("")}>
      {/* Header and Billing Switch */}
      <div className="flex w-full flex-col items-center justify-between md:flex-row xl:mb-10">
        <nav className="my-4">
          <div className={cn("flex items-center text-sm text-gray-600")}>
            {(["selection", "payment", "confirmation"] as Step[]).map(
              (currentStep, index) => (
                <div key={currentStep} className="flex items-center">
                  <TextComponent
                    type="caption"
                    className={
                      step === currentStep
                        ? "font-semibold text-kd-lte-blue"
                        : ""
                    }
                  >
                    {currentStep.charAt(0).toUpperCase() + currentStep.slice(1)}
                  </TextComponent>
                  {index < 2 && (
                    <HiOutlineChevronRight className="mx-2 size-[18px] text-[#979797]" />
                  )}
                </div>
              ),
            )}
          </div>
        </nav>
        <BillingToggle
          isMonthly={billing === "monthly"}
          setIsMonthly={(value) => setBilling(value ? "monthly" : "annually")}
        />
      </div>

      <div className="flex flex-row justify-center space-x-[23px] xl:flex-col xl:space-x-0">
        {/* Pricing Cards */}
        <div
          className={cn(
            "flex flex-col items-center justify-between xl:space-x-[20px] xl:flex-row xl:space-y-0",
          )}
        >
          {/* Special introductory card */}
          <div
            className={cn(
              "flex flex-col justify-between rounded-[15px] xl:rounded-[20px] relative bg-transparent p-6 ",
              "xl:w-[300px] xl:h-[396px] w-[225px] h-[300px]",
              "2xl:flex hidden",
            )}
          >
            <div className="">
              <TextComponent
                type="h3"
                className="mt-[28px] text-[23px] font-semibold text-kd-secondary-1 xl:mt-[38px]"
              >
                Pricing Plans for Every Budget
              </TextComponent>

              <TextComponent
                type="caption"
                className="mt-4 text-sm text-kd-secondary-2"
              >
                Get ready to unlock all the{" "}
                <span className="font-semibold">
                  campaign enhancing features
                </span>{" "}
                you need to escalate your email strategies, measure performance,
                and deliver Month-on-Month, email campaign growth. No surprises,
                just transparent pricing for real results.
              </TextComponent>
            </div>
            <Button
              variant="secondary"
              onClick={() => {
                setShowFeatureMatrix(!showFeatureMatrix);
              }}
              className="w-fit"
            >
              {showFeatureMatrix ? "Hide features" : "Show features"}
            </Button>
          </div>

          {/* Existing cards */}
          {items.map((item, index) => {
            const planName =
              index === 0 ? "basic" : index === 1 ? "standard" : "premium";
            const billingCycleStr = billing === "monthly" ? "monthly" : "annual";
            const buttonId = `upgrade_${planName}_${billingCycleStr}_click`;
            const isFreePlanCard = index === 0;
            return (
              <div
                key={index}
                className={cn(
                  "flex flex-col justify-between rounded-[15px] xl:rounded-[20px] border relative bg-white p-6 shadow-lg",
                  "xl:w-[300px] xl:h-[396px] w-[225px] h-[300px]",
                  index === 1
                    ? "border-t-0 rounded-t-none mt-[30px]"
                    : "border-kd-secondary-6 mt-[14px] mb-[14px] xl:mt-0 xl:mb-0",
                  index === selectedCardIndex
                    ? "border-kd-lte-blue border"
                    : "",
                )}
                onClick={() => setSelectedCardIndex(index)}
              // onMouseOver={() => setSelectedCardIndex(index)}
              >
                {/* Best value label */}
                {index === 1 && (
                  <div className="absolute -top-7 right-0 flex h-10 w-full items-center justify-center rounded-t-[20px] bg-kd-lte-blue ring-2 ring-kd-lte-blue">
                    <TextComponent
                      type="body"
                      className="text-center font-semibold text-white"
                    >
                      Best Value
                    </TextComponent>
                  </div>
                )}
                <div>
                  <TextComponent type="h3" className="text-kd-secondary-1">
                    <PrismicRichText field={item.title} />
                  </TextComponent>

                  {/* Strikethrough original monthly price - only shown for annual billing */}
                  {/* Placeholder div maintains consistent height when toggling between monthly/annual */}
                  <TextComponent
                    type="body"
                    className={
                      !isFreePlanCard && billing === "annually"
                        ? "mt-4 pb-1 text-kd-secondary-3 line-through decoration-[#FA5A7D]"
                        : "mt-4 pb-1 invisible"
                    }
                  >
                    {isFreePlanCard
                      ? ""
                      : `was $${item.monthly_price[0].text} /month`}
                  </TextComponent>
                  <div className="mt-1 flex items-baseline text-kd-lte-blue xl:mt-2">
                    {isFreePlanCard ? (
                      <TextComponent
                        type="h2"
                        className="text-[35px] font-semibold leading-[24px] xl:text-[40px] xl:leading-[32px]"
                      >
                        Free
                      </TextComponent>
                    ) : (
                      <>
                        <TextComponent
                          type="h2"
                          className="text-[35px] font-semibold leading-[24px] xl:text-[40px] xl:leading-[32px]"
                        >
                          <sup className="text-[20px] xl:text-[24px]">$</sup>
                          {billing === "monthly"
                            ? item.monthly_price[0].text
                            : item.annual_price[0].text}
                        </TextComponent>
                        <TextComponent
                          type="body"
                          className=" ml-1 text-left font-semibold"
                        >
                          / month
                        </TextComponent>
                      </>
                    )}
                  </div>
                  <TextComponent
                    type="body"
                    className="mt-2 font-medium leading-[14px] text-kd-lte-blue xl:leading-[20px]"
                  >
                    {isFreePlanCard ? (
                      "Always free"
                    ) : (
                      <PrismicRichText
                        field={
                          billing === "monthly"
                            ? item.monthly_text
                            : item.annually_text
                        }
                      />
                    )}
                  </TextComponent>
                  <div className="mt-4">
                    <TextComponent
                      className="font-semibold text-black"
                      type="caption"
                    >
                      <PrismicRichText field={item.subtitle} />
                    </TextComponent>
                    <TextComponent type="caption">
                      <PrismicRichText field={item.description} />
                    </TextComponent>
                  </div>
                </div>
                {(() => {
                  const planType =
                    index === 0
                      ? "BASIC"
                      : index === 1
                        ? "STANDARD"
                        : "PREMIUM";

                  // Check if user is on a trial premium plan (not a paid premium subscription)
                  const isOnTrialPremiumPlan =
                    subscriptionPlan?.toUpperCase().includes("TRIAL") &&
                    subscriptionPlan?.toUpperCase().includes("PREMIUM");

                  // Show "Your current plan" only if:
                  // 1. The subscription plan matches this card's plan type
                  // 2. AND it's NOT a trial premium plan (or if it is a trial, only show for non-premium cards)
                  if (subscriptionPlan?.toUpperCase().includes(planType)) {
                    if (planType === "PREMIUM" && isOnTrialPremiumPlan) {
                      // Don't show "Your current plan" for Premium card when on trial
                      // Fall through to show the button instead
                    } else {
                      return (
                        <TextComponent
                          type="body"
                          className="text-center font-medium text-gray-600"
                        >
                          Your current plan
                        </TextComponent>
                      );
                    }
                  }

                  // Determine if this is a higher or lower tier plan
                  const currentPlanIndex = subscriptionPlan
                    ?.toUpperCase()
                    .includes("BASIC")
                    ? 0
                    : subscriptionPlan?.toUpperCase().includes("STANDARD")
                      ? 1
                      : subscriptionPlan?.toUpperCase().includes("PREMIUM")
                        ? 2
                        : -1;

                  const buttonLabel = isFreePlanCard
                    ? "Switch to Free"
                    : index > currentPlanIndex
                      ? "Upgrade"
                      : "Buy Now";

                  return (
                    <Button
                      id={buttonId}
                      onClick={() => {
                        const billingCycle =
                          billing === "monthly" ? "MONTHLY" : "ANNUAL";
                        trackGAEvent(buttonId, {});
                        onSelectPlan(planType, billingCycle);
                      }}
                    >
                      {buttonLabel}
                    </Button>
                  );
                })()}
              </div>
            );
          })}
        </div>

        {showFeatureMatrix && (
          <div className="mt-4 hidden w-full md:block">
            <FeatureMatrixComponent selectedCardIndex={selectedCardIndex} />
          </div>
        )}
      </div>
    </div>
  );
}